import { AlertTriangle, Mail, MailX } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { z } from "zod";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaPasswordField } from "@/shared/components/forms/ColocaPasswordField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { ColocaDialog, ColocaDialogClose } from "@/shared/components/overlays/ColocaDialog";
import { useStepUpEmailBackup } from "@/shared/hooks/useStepUpEmailBackup";
import { useTranslations } from "@/shared/lang/useTranslations";
import type { StepUpMethod } from "@/shared/types/user.entity";
import { colocaToast } from "@/shared/utils/toast";

interface EmailBackupDialogProps {
  /** What the switch asks for: turning the backup on (true) or off (false). Null keeps the dialog closed. */
  target: boolean | null;
  /** The method the backup backs: the "on" copy differs, because with the app it costs a little security. */
  method: StepUpMethod;
  email: string;
  onClose: () => void;
}

/**
 * The switch of the email backup never flips on its own: it opens this dialog, which says what changes and
 * takes the account password — the same proof that created the method it backs.
 */
export function EmailBackupDialog({ target, ...rest }: EmailBackupDialogProps) {
  return target === null ? null : <EmailBackupDialogContent enable={target} {...rest} />;
}

function EmailBackupDialogContent({
  enable,
  method,
  email,
  onClose,
}: Omit<EmailBackupDialogProps, "target"> & { enable: boolean }) {
  const { t } = useTranslations("settings");
  const { t: tg } = useTranslations("global");
  const { setEnabled, saving } = useStepUpEmailBackup();
  const formId = useId();
  const [formError, setFormError] = useState<string | null>(null);

  const schema = useMemo(
    () => z.object({ password: z.string().min(1, tg("security_pin_password_required")) }),
    [tg],
  );
  const form = useZodForm(schema, { password: "" });

  const submit = async ({ password }: { password: string }) => {
    setFormError(null);
    const failure = await setEnabled(enable, password);
    if (!failure) {
      colocaToast.success(
        t(enable ? "security_email_backup_enabled_toast" : "security_email_backup_disabled_toast"),
      );
      onClose();
      return;
    }
    if (failure.field === "password") {
      form.setError("password", { message: failure.message }, { shouldFocus: true });
      return;
    }
    setFormError(failure.message);
  };

  const body = !enable
    ? t("security_email_backup_disable_body")
    : method === "totp"
      ? t("security_email_backup_enable_body_totp", { email })
      : t("security_email_backup_enable_body_pin", { email });

  return (
    <ColocaDialog
      open
      onOpenChange={(next) => {
        if (!next && saving) return;
        if (!next) onClose();
      }}
      showCloseButton={!saving}
      size="sm"
      title={
        <span className="flex items-center gap-2">
          {enable ? (
            <Mail className="h-5 w-5 text-primary" aria-hidden="true" />
          ) : (
            <MailX className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
          )}
          {t(enable ? "security_email_backup_enable_title" : "security_email_backup_disable_title")}
        </span>
      }
      footer={
        <>
          <ColocaDialogClose render={<ColocaButton variant="ghost" disabled={saving} />}>
            {t("security_email_backup_cancel")}
          </ColocaDialogClose>
          <ColocaButton variant="primary" type="submit" form={formId} loading={saving}>
            {t(enable ? "security_email_backup_enable_confirm" : "security_email_backup_disable_confirm")}
          </ColocaButton>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {/* With the app, turning it on is a trade: the dialog says so before the password, not after. */}
        <ColocaAlert
          variant={enable && method === "totp" ? "warning" : "secondary"}
          icon={enable && method === "totp" ? AlertTriangle : Mail}
          text={body}
        />
        {formError ? <ColocaAlert variant="destructive" icon={AlertTriangle} text={formError} /> : null}
        <ColocaForm form={form} onSubmit={submit} id={formId} className="space-y-2">
          <p className="text-[12.5px] text-muted-foreground">{t("security_email_backup_password_intro")}</p>
          <ColocaPasswordField
            name="password"
            label={tg("security_pin_password_label")}
            autoComplete="current-password"
            showLabel={tg("security_pin_password_show")}
            hideLabel={tg("security_pin_password_hide")}
          />
        </ColocaForm>
      </div>
    </ColocaDialog>
  );
}
